// Das Layout der Welt mit Parzellen (docs/architektur-paket-2.md, Abschnitt „Welt mit
// Parzellen“, ADR 0023): reine Geometrie, ohne einen einzigen Block. `world.ts` baut daraus die
// Welt, der Server verteilt die Parzellen an Konten, der Client malt die Schilder.
//
// Aufbau: Das Dorf aus `generateVillage()` liegt in der Mitte. Von seinem Platz führen vier Wege
// nach Norden, Süden, Osten und Westen bis zur Hecke. An jedem Weg liegen sechs Parzellen, drei
// je Seite, hintereinander, mit einer Erweiterungszone dazwischen. Eine Parzelle ist ein
// Quadrat: der Zaun liegt auf dem Rand, innen bleiben 20 × 20 Blöcke Hof.
//
// Innerhalb einer Parzelle wird in **lokalen** Koordinaten geplant, damit alle vier
// Torrichtungen denselben Bauplan bekommen: `u` zählt vom Tor in den Hof hinein (0 = die Zelle
// innen am Tor, 19 = die hinterste), `v` läuft quer dazu von links nach rechts, wenn man im Tor
// steht und hineinschaut. `parcelLocalToWorld` bildet das auf Weltkoordinaten ab; u = −1 ist die
// Zaunzelle selbst, u = −2 und −3 der freie Streifen zum Weg.

import { CHUNK_SIZE } from './chunk.js';
import type { Ausrichtung, Pen, Rechteck, SpawnPoint } from './types.js';
import { DORF, yawZu } from './village.js';

export type { Ausrichtung, Rechteck } from './types.js';

/** Parameter des Weltgenerators. `parcels` ist die Zahl der Hof-Parzellen; Standard 24. */
export interface WorldParams {
  parcels: number;
}

/** Vorbelegung: vierundzwanzig Parzellen, sechs je Weg. */
export const DEFAULT_WORLD_PARAMS: WorldParams = { parcels: 24 };

/** Die Maße einer Parzelle und ihrer Umgebung, alle in Blöcken. */
export const PARZELLE = {
  /** Begehbare Kantenlänge innerhalb des Zauns. */
  innen: 20,
  /** Freie Blöcke zwischen Zaun und Weg; darauf liegt der Pflasterpfad zum Tor. */
  abstandWeg: 2,
  /** Erweiterungszone zwischen zwei Parzellen einer Reihe. */
  abstandNachbar: 6,
  /** Versatz der Zaunlinie vom Dorfrand; frei bleiben davon ein Block weniger. */
  abstandDorf: 8,
  /** Parzellen je Wegseite. */
  jeSeite: 3,
  /** Die Zelle der Zaunlinie, in der das Tor sitzt (lokale v-Koordinate). */
  torV: 10,
} as const;

/** Kantenlänge einer Parzelle einschließlich Zaun. */
export const PARZELLE_SEITE = PARZELLE.innen + 2;

/**
 * So viele Zellen bleiben rings um die Zaunlinie auf Fußhöhe frei von begehbaren Blöcken
 * (ADR 0026). Ein Zaun ist 1,5 Blöcke hoch, der Sprung trägt 1,26 – vom Boden aus kommt niemand
 * darüber. Steht aber ein Block zum Draufsteigen daneben, beginnt der Sprung einen Block höher
 * und führt über den Zaun. Drei freie Zellen reichen: Wer von weiter weg anläuft, ist längst
 * wieder auf dem Boden, wenn er den Zaun erreicht. `world.test.ts` prüft die Regel für jede
 * Parzelle, `step.test.ts` beide Hälften der Physik dahinter.
 */
export const ZAUN_FREIRAUM = 3;

/** Vier Wege, zwei Seiten, drei Parzellen je Seite. Mehr passt nicht ins Layout. */
export const MAX_PARCELS = 4 * 2 * PARZELLE.jeSeite;

/**
 * Chunks je Arm neben dem Dorf. Eine Reihe braucht `3 * 22 + 2 * 6 = 78` Blöcke, dazu Abstand zum
 * Dorf und zur Hecke; sechs Chunks (96 Blöcke) reichen dafür, vier nicht (ADR 0023).
 */
export const ARM_CHUNKS = 6;

/** Kantenlänge der ganzen Welt in Chunks: Dorf plus je ein Arm nach beiden Seiten. */
export const WELT_CHUNKS = DORF.chunks + 2 * ARM_CHUNKS;

/** Kantenlänge der ganzen Welt in Blöcken. */
export const WELT_BLOECKE = WELT_CHUNKS * CHUNK_SIZE;

/** Versatz des Dorfes in der großen Welt: seine West-Nord-Ecke. */
export const DORF_VERSATZ = ARM_CHUNKS * CHUNK_SIZE;

/** Das Dorf als Rechteck in Weltkoordinaten; Parzellen und Wege halten sich davon frei. */
export const DORF_RECHTECK: Rechteck = {
  minX: DORF_VERSATZ,
  minZ: DORF_VERSATZ,
  maxX: DORF_VERSATZ + DORF.bloecke - 1,
  maxZ: DORF_VERSATZ + DORF.bloecke - 1,
};

/** Die Hecke liegt auf dem äußersten Ring; die Wege beginnen einen Block dahinter. */
const HECKE = 1;

/** Der Nord-Süd-Weg als Rechteck über die ganze Welt (Nord- und Südweg in einem Streifen). */
const WEG_NORD_SUED: Rechteck = {
  minX: DORF_VERSATZ + DORF.wegX.min,
  maxX: DORF_VERSATZ + DORF.wegX.max,
  minZ: HECKE,
  maxZ: WELT_BLOECKE - 1 - HECKE,
};

/** Der Ost-West-Weg als Rechteck über die ganze Welt. */
const WEG_OST_WEST: Rechteck = {
  minX: HECKE,
  maxX: WELT_BLOECKE - 1 - HECKE,
  minZ: DORF_VERSATZ + DORF.querWegZ.min,
  maxZ: DORF_VERSATZ + DORF.querWegZ.max,
};

/**
 * Die gepflasterten Wege der Welt. Es sind zwei Streifen, die sich auf dem Dorfplatz kreuzen –
 * und damit die vier Wege nach Norden, Süden, Osten und Westen.
 */
export const WEGE: readonly Rechteck[] = [WEG_NORD_SUED, WEG_OST_WEST];

/** Ein Punkt in Blockkoordinaten; `y` ist immer die Fußhöhe der Parzelle. */
export interface Punkt {
  x: number;
  y: number;
  z: number;
}

/**
 * Eine Hof-Parzelle als reine Daten. `minX`..`maxZ` ist die Zaunlinie einschließlich; innerhalb
 * liegt der Hof (`parcelPen`). Alle Punkte sind Blockkoordinaten, keine Mittelpunkte.
 */
export interface Parcel {
  id: number;
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
  /** Fußhöhe: y des Blocks, auf dem Zaun, Tiere und Bauteile stehen. */
  y: number;
  /** An welchem der vier Wege die Parzelle liegt. */
  weg: Ausrichtung;
  /** Auf welcher Seite des Zauns das Tor sitzt; das ist zugleich die Richtung zum Weg. */
  gateFacing: Ausrichtung;
  /** Der Torblock auf der Zaunlinie. */
  gate: Punkt;
  /** Das Schild in der Zaunlinie neben dem Tor; `facing` blickt zum Weg. */
  sign: Punkt & { facing: Ausrichtung };
  /** Standplatz der Kuh im Unterstand. */
  shelter: Punkt;
  /** Die Kiste, innen neben dem Tor. */
  chest: Punkt;
  /** Der Fuß des Apfelbaumstamms; die Äpfel hängen in der Krone darüber. */
  tree: Punkt;
  /** Kennung der Koppel: `parzelle-<id>`. */
  penId: string;
  /** Die Fläche, auf die die Parzelle später wachsen kann; sie überlappt keine andere. */
  expansion: Rechteck;
}

/** Der Bauplan innerhalb einer Parzelle, in lokalen Koordinaten (u vom Tor nach hinten, v quer). */
export const HOF_PLAN = {
  /** Unterstand: hintere Ecke links, offene Front zum Tor. */
  unterstand: { uMin: 15, uMax: 18, vMin: 0, vMax: 4 },
  /** Standplatz der Kuh im Unterstand. */
  standplatz: { u: 17, v: 2 },
  /** Hühnerstall: hintere Ecke rechts, reine Kulisse. */
  huehnerstall: { uMin: 16, uMax: 18, vMin: 16, vMax: 19 },
  /** Apfelbaum: vorn links, das 5 × 5 große Bauteil um den Stamm herum. */
  apfelbaum: { uMin: 2, uMax: 6, vMin: 1, vMax: 5 },
  /** Der Stamm des Apfelbaums, in der Mitte des Bauteils. */
  stamm: { u: 4, v: 3 },
  /** Feld: 4 × 6 Acker seitlich rechts. */
  feld: { uMin: 7, uMax: 12, vMin: 15, vMax: 18 },
  /**
   * Kiste: innen beim Tor, aber mit `ZAUN_FREIRAUM` freien Zellen zur Zaunlinie – direkt am Zaun
   * wäre sie die Stufe, von der aus der Sprung über ihn trüge (ADR 0026).
   */
  kiste: { u: ZAUN_FREIRAUM, v: PARZELLE.torV + 1 },
  /**
   * Schild: in der Zaunlinie unmittelbar neben dem Tor, mit Blick zum Weg. Es ist Teil des Zauns
   * und darum genauso hoch (Block `schild`, `ZAUN_HOEHE`); außerhalb davor stünde es als
   * Trittstein am Zaun (ADR 0026).
   */
  schild: { u: -1, v: PARZELLE.torV - 1 },
  /** Der Pflasterpfad vom Weg zum Tor. */
  pfad: { uVon: -3, uBis: -2, v: PARZELLE.torV },
  /** Startplatz der Kuh: freie Weide in der Mitte, weit weg von allen Bauteilen. */
  weide: { u: 10, v: 10 },
  /** Startpunkt eines neuen Kontos: innen direkt vor dem Tor. */
  spawn: { u: 0, v: PARZELLE.torV },
} as const;

/** Ein Weg und die Richtung, in der er das Dorf verlässt. */
interface WegPlan {
  richtung: Ausrichtung;
  /** Der Querstreifen des Weges (die Achse quer zur Laufrichtung). */
  quer: { min: number; max: number };
  /** Blockkoordinate des Dorfrandes, von der aus nach außen gezählt wird. */
  dorfRand: number;
  /** In welche Richtung „nach außen“ zeigt. */
  aussen: 1 | -1;
  /** Ob die Laufrichtung des Weges die z-Achse ist (Nord und Süd) oder die x-Achse. */
  achseIstZ: boolean;
}

/**
 * Die vier Wege in der Reihenfolge, in der die Parzellen vergeben werden. Reihum um das Dorf,
 * damit die ersten Kennungen gleichmäßig verteilt liegen.
 */
const WEG_PLAENE: readonly WegPlan[] = [
  { richtung: 'n', quer: { min: WEG_NORD_SUED.minX, max: WEG_NORD_SUED.maxX }, dorfRand: DORF_RECHTECK.minZ, aussen: -1, achseIstZ: true },
  { richtung: 'e', quer: { min: WEG_OST_WEST.minZ, max: WEG_OST_WEST.maxZ }, dorfRand: DORF_RECHTECK.maxX, aussen: 1, achseIstZ: false },
  { richtung: 's', quer: { min: WEG_NORD_SUED.minX, max: WEG_NORD_SUED.maxX }, dorfRand: DORF_RECHTECK.maxZ, aussen: 1, achseIstZ: true },
  { richtung: 'w', quer: { min: WEG_OST_WEST.minZ, max: WEG_OST_WEST.maxZ }, dorfRand: DORF_RECHTECK.minX, aussen: -1, achseIstZ: false },
];

/**
 * Alle Parzellen der Welt, deterministisch und immer in derselben Reihenfolge. Die Kennungen
 * laufen von 1 an; die niedrigen liegen am Dorf, weil Reihe für Reihe vergeben wird und ein
 * neues Konto stets die niedrigste freie Parzelle bekommt.
 */
export function parcelLayout(params: WorldParams = DEFAULT_WORLD_PARAMS): Parcel[] {
  const anzahl = params.parcels;
  if (!Number.isInteger(anzahl) || anzahl < 0 || anzahl > MAX_PARCELS) {
    throw new Error(`Es sind 0 bis ${MAX_PARCELS} Parzellen möglich, verlangt waren ${anzahl}.`);
  }
  const parzellen: Parcel[] = [];
  for (let reihe = 0; reihe < PARZELLE.jeSeite && parzellen.length < anzahl; reihe++) {
    for (const weg of WEG_PLAENE) {
      for (const seite of [-1, 1] as const) {
        if (parzellen.length >= anzahl) break;
        parzellen.push(baueParzellenDaten(parzellen.length + 1, weg, seite, reihe));
      }
    }
  }
  return parzellen;
}

/** Die Koppel einer Parzelle: die Fläche innerhalb des Zauns. */
export function parcelPen(parcel: Parcel): Pen {
  return {
    id: parcel.penId,
    minX: parcel.minX + 1,
    minZ: parcel.minZ + 1,
    maxX: parcel.maxX - 1,
    maxZ: parcel.maxZ - 1,
    y: parcel.y,
  };
}

/**
 * Bildet eine lokale Zelle auf Weltkoordinaten ab. `u` zählt vom Tor in den Hof (0 = innen am
 * Tor, −1 = die Zaunzelle des Tores), `v` quer dazu von links nach rechts, wenn man im Tor steht.
 */
export function parcelLocalToWorld(parcel: Parcel, u: number, v: number): { x: number; z: number } {
  const ix0 = parcel.minX + 1;
  const iz0 = parcel.minZ + 1;
  const ix1 = parcel.maxX - 1;
  const iz1 = parcel.maxZ - 1;
  switch (parcel.gateFacing) {
    case 's':
      return { x: ix0 + v, z: iz1 - u };
    case 'n':
      return { x: ix1 - v, z: iz0 + u };
    case 'e':
      return { x: ix1 - u, z: iz1 - v };
    case 'w':
      return { x: ix0 + u, z: iz0 + v };
  }
}

/** Vierteldrehungen, mit denen ein nach Süden offenes Bauteil zum Tor dieser Parzelle blickt. */
export function parcelPrefabDrehung(parcel: Parcel): number {
  switch (parcel.gateFacing) {
    case 's':
      return 0;
    case 'w':
      return 1;
    case 'n':
      return 2;
    case 'e':
      return 3;
  }
}

/** Das Weltrechteck zu einem lokalen Rechteck der Parzelle. */
export function parcelLocalRect(parcel: Parcel, uMin: number, uMax: number, vMin: number, vMax: number): Rechteck {
  const a = parcelLocalToWorld(parcel, uMin, vMin);
  const b = parcelLocalToWorld(parcel, uMax, vMax);
  return {
    minX: Math.min(a.x, b.x),
    maxX: Math.max(a.x, b.x),
    minZ: Math.min(a.z, b.z),
    maxZ: Math.max(a.z, b.z),
  };
}

/**
 * Startpunkt eines Kontos auf seiner Parzelle: in der Mitte der Zelle innen vor dem Tor, mit
 * Blick in den Hof.
 */
export function parcelSpawn(parcel: Parcel): SpawnPoint {
  const zelle = parcelLocalToWorld(parcel, HOF_PLAN.spawn.u, HOF_PLAN.spawn.v);
  const tief = parcelLocalToWorld(parcel, HOF_PLAN.spawn.u + 1, HOF_PLAN.spawn.v);
  return {
    x: zelle.x + 0.5,
    y: parcel.y,
    z: zelle.z + 0.5,
    yaw: yawZu(zelle.x + 0.5, zelle.z + 0.5, tief.x + 0.5, tief.z + 0.5),
  };
}

/** Legt die Geometrie einer Parzelle fest; `seite` ist −1 für die kleinere Querkoordinate. */
function baueParzellenDaten(id: number, weg: WegPlan, seite: -1 | 1, reihe: number): Parcel {
  // Entlang des Weges: die Reihen liegen mit gleichem Abstand hintereinander, vom Dorf nach außen.
  const nah = weg.dorfRand + weg.aussen * (PARZELLE.abstandDorf + reihe * (PARZELLE_SEITE + PARZELLE.abstandNachbar));
  const fern = nah + weg.aussen * (PARZELLE_SEITE - 1);
  const achse = { min: Math.min(nah, fern), max: Math.max(nah, fern) };

  // Quer zum Weg: der Zaun steht `abstandWeg` Blöcke neben dem Pflaster.
  const quer =
    seite === -1
      ? { max: weg.quer.min - 1 - PARZELLE.abstandWeg, min: weg.quer.min - 1 - PARZELLE.abstandWeg - (PARZELLE_SEITE - 1) }
      : { min: weg.quer.max + 1 + PARZELLE.abstandWeg, max: weg.quer.max + 1 + PARZELLE.abstandWeg + (PARZELLE_SEITE - 1) };

  // Das Tor blickt zum Weg, also auf die dem Weg zugewandte Seite des Zauns.
  const gateFacing: Ausrichtung = weg.achseIstZ
    ? seite === -1
      ? 'e'
      : 'w'
    : seite === -1
      ? 's'
      : 'n';

  const rahmen = weg.achseIstZ
    ? { minX: quer.min, maxX: quer.max, minZ: achse.min, maxZ: achse.max }
    : { minX: achse.min, maxX: achse.max, minZ: quer.min, maxZ: quer.max };

  // Die Erweiterungszone: die halbe Lücke zu jedem Nachbarn der Reihe und ein Streifen hinter dem
  // Hof, weg vom Weg. Zum Weg hin bleibt alles frei, dort liegt der Pfad zum Tor.
  const halbeLuecke = PARZELLE.abstandNachbar / 2;
  const hinten = PARZELLE.abstandNachbar;
  const expansion = weg.achseIstZ
    ? {
        minX: seite === -1 ? rahmen.minX - hinten : rahmen.minX,
        maxX: seite === -1 ? rahmen.maxX : rahmen.maxX + hinten,
        minZ: rahmen.minZ - halbeLuecke,
        maxZ: rahmen.maxZ + halbeLuecke,
      }
    : {
        minX: rahmen.minX - halbeLuecke,
        maxX: rahmen.maxX + halbeLuecke,
        minZ: seite === -1 ? rahmen.minZ - hinten : rahmen.minZ,
        maxZ: seite === -1 ? rahmen.maxZ : rahmen.maxZ + hinten,
      };

  const grundriss = { id, ...rahmen, y: DORF.fussY, weg: weg.richtung, gateFacing, penId: `parzelle-${id}` };
  // Die Punkte des Hofs entstehen aus dem lokalen Bauplan; dafür genügt der Rahmen samt Torseite.
  const roh: Parcel = {
    ...grundriss,
    gate: { x: 0, y: DORF.fussY, z: 0 },
    sign: { x: 0, y: DORF.fussY, z: 0, facing: gateFacing },
    shelter: { x: 0, y: DORF.fussY, z: 0 },
    chest: { x: 0, y: DORF.fussY, z: 0 },
    tree: { x: 0, y: DORF.fussY, z: 0 },
    expansion,
  };

  const punkt = (u: number, v: number): Punkt => {
    const zelle = parcelLocalToWorld(roh, u, v);
    return { x: zelle.x, y: DORF.fussY, z: zelle.z };
  };

  return {
    ...roh,
    gate: punkt(-1, PARZELLE.torV),
    sign: { ...punkt(HOF_PLAN.schild.u, HOF_PLAN.schild.v), facing: gateFacing },
    shelter: punkt(HOF_PLAN.standplatz.u, HOF_PLAN.standplatz.v),
    chest: punkt(HOF_PLAN.kiste.u, HOF_PLAN.kiste.v),
    tree: punkt(HOF_PLAN.stamm.u, HOF_PLAN.stamm.v),
  };
}
